import { useEffect, useState } from 'react';
import {
  Box, Button, Checkbox, Chip, CircularProgress, Divider, FormControlLabel, IconButton, Link, MenuItem,
  Paper, Radio, RadioGroup, Slider, Stack, TextField, Typography,
} from '@mui/material';
import { Close, LockOutlined, Place } from '@mui/icons-material';
import { CTWA_TARGETING_SEARCH } from '../../../config/urls';
import { ColorsConstants } from '../../../cssUtils/constants';
import { useCtwaApi } from './api';
import { CITY_RADIUS_OPTIONS, COUNTRIES, LOCKED_AGE, PIN_RADIUS_DEFAULT, SPECIAL_CATEGORY_MIN_RADIUS_KM } from './constants';
import { isLockedCategory } from './validation';
import { FieldLabel, Note } from './ui';

const radioSx = { color: ColorsConstants.primaryBlue100, '&.Mui-checked': { color: ColorsConstants.primaryBlue100 } };
const TYPE_LABEL = { region: 'STATE', city: 'CITY', zip: 'PINCODE', custom: 'PIN' };

// Debounced search against the targeting search endpoint; returns [results, loading].
const useSearch = (params, query) => {
  const api = useCtwaApi();
  const [state, setState] = useState([[], false]);
  const qs = new URLSearchParams({ ...params, q: query.trim() }).toString();
  useEffect(() => {
    if (query.trim().length < 2) { setState([[], false]); return undefined; }
    setState(([r]) => [r, true]);
    const timer = setTimeout(() => {
      api.get(`${CTWA_TARGETING_SEARCH}?${qs}`)
        .then((d) => setState([d.results || [], false]))
        .catch(() => setState([[], false]));
    }, 350);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qs]);
  return state;
};

const ResultList = ({ results, onPick, render }) => results.length > 0 && (
  <Paper variant="outlined" sx={{ maxHeight: 220, overflow: 'auto', mt: 0.5, borderRadius: 1.5 }}>
    {results.map((r) => (
      <Box key={r.key || r.id} component="button" type="button" onClick={() => onPick(r)}
        sx={{ display: 'block', width: '100%', textAlign: 'left', font: 'inherit', border: 0, background: 'white', px: 2, py: 1, cursor: 'pointer', borderBottom: `1px solid ${ColorsConstants.primaryGray10}`, '&:hover': { backgroundColor: ColorsConstants.primaryBlue60 } }}>
        {render(r)}
      </Box>
    ))}
  </Paper>
);

const PinForm = ({ onAdd, onCancel, minRadius = 1 }) => {
  const [pin, setPin] = useState({ name: '', latitude: '', longitude: '', radiusKm: Math.max(PIN_RADIUS_DEFAULT, minRadius) });
  const lat = Number(pin.latitude);
  const lng = Number(pin.longitude);
  const valid = pin.latitude !== '' && pin.longitude !== '' && Math.abs(lat) <= 90 && Math.abs(lng) <= 180 && pin.radiusKm >= minRadius;
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      {/* ponytail: coordinates instead of a map widget (no map library in the app); add one if users struggle. */}
      <Note sx={{ mb: 1.5 }}>
        Find the place on <Link href="https://www.google.com/maps" target="_blank" rel="noopener noreferrer">Google Maps</Link>,
        right-click it and copy the coordinates.
      </Note>
      <Stack direction="row" flexWrap="wrap" gap={1.5}>
        <TextField size="small" label="Label" value={pin.name} onChange={(e) => setPin({ ...pin, name: e.target.value })} sx={{ flex: '1 1 160px' }} />
        <TextField size="small" label="Latitude" value={pin.latitude} onChange={(e) => setPin({ ...pin, latitude: e.target.value })} sx={{ width: 130 }} />
        <TextField size="small" label="Longitude" value={pin.longitude} onChange={(e) => setPin({ ...pin, longitude: e.target.value })} sx={{ width: 130 }} />
        <TextField size="small" type="number" label="Radius (km)" value={pin.radiusKm} inputProps={{ min: minRadius, max: 80 }}
          onChange={(e) => setPin({ ...pin, radiusKm: Math.min(80, Number(e.target.value)) })} sx={{ width: 120 }} />
      </Stack>
      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
        <Button size="small" variant="contained" disabled={!valid} disableElevation
          onClick={() => onAdd({ type: 'custom', key: `pin:${lat},${lng}`, name: pin.name || `${lat}, ${lng}`, latitude: lat, longitude: lng, radiusKm: pin.radiusKm })}>
          Add pin
        </Button>
        <Button size="small" onClick={onCancel}>Cancel</Button>
      </Stack>
    </Paper>
  );
};

const TargetingSection = ({ targeting: t, specialAdCategory, retiredIds = [], onChange }) => {
  const [locQuery, setLocQuery] = useState('');
  const [detQuery, setDetQuery] = useState('');
  const [showPin, setShowPin] = useState(false);
  const locked = isLockedCategory(specialAdCategory);
  const minRadius = locked ? SPECIAL_CATEGORY_MIN_RADIUS_KM : 0;

  const [locResults, locLoading] = useSearch({ type: 'adlocation', country: t.country }, locQuery);
  const [detResults, detLoading] = useSearch({ type: 'addetailed', country: t.country, special_ad_category: specialAdCategory }, detQuery);

  const addLocation = (loc) => {
    if (!t.locations.some((l) => l.key === loc.key)) {
      onChange({ locations: [...t.locations, { ...loc, radiusKm: loc.type === 'city' ? minRadius : loc.radiusKm }] });
    }
    setLocQuery('');
  };
  const setLocation = (key, patch) => onChange({ locations: t.locations.map((l) => (l.key === key ? { ...l, ...patch } : l)) });

  const addDetailed = (d) => {
    if (!t.detailed.some((x) => x.id === d.id)) onChange({ detailed: [...t.detailed, { id: d.id, name: d.name, type: d.type }] });
    setDetQuery('');
  };

  const retired = t.detailed.filter((d) => retiredIds.includes(d.id));

  return (
    <Stack spacing={2.5}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: ColorsConstants.primaryBlue100, backgroundColor: ColorsConstants.primaryBlue60 }}>
        <FormControlLabel
          control={<Checkbox checked={t.advantageAudience} sx={radioSx} onChange={(e) => onChange({ advantageAudience: e.target.checked })} />}
          label={
            <Box>
              <Typography variant="body2" fontWeight={700}>Advantage+ audience (recommended)</Typography>
              <Note>Your age and interest choices act as suggestions. Meta can reach beyond them when it finds people likely to chat. Location always stays fixed.</Note>
            </Box>
          }
        />
      </Paper>

      <Box>
        <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>Location</Typography>
        <Stack direction="row" flexWrap="wrap" gap={1.5} alignItems="flex-start">
          <Box sx={{ width: 200 }}>
            <FieldLabel>Country</FieldLabel>
            <TextField select fullWidth size="small" value={t.country}
              onChange={(e) => onChange({ country: e.target.value, locations: [], detailed: [] })}>
              {COUNTRIES.map((c) => <MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>)}
            </TextField>
          </Box>
          <Box sx={{ flex: '1 1 280px' }}>
            <FieldLabel>Add state, city or pincode</FieldLabel>
            <TextField fullWidth size="small" type="search" placeholder="e.g. Pune, 400053, Gujarat" value={locQuery}
              onChange={(e) => setLocQuery(e.target.value)}
              InputProps={{ endAdornment: locLoading ? <CircularProgress size={16} /> : null }} />
            <ResultList results={locResults} onPick={addLocation} render={(r) => (
              <Stack direction="row" alignItems="center" gap={1}>
                <Chip size="small" label={TYPE_LABEL[r.type] || r.type} sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
                <Typography variant="body2">{r.name}</Typography>
                <Typography variant="caption" color={ColorsConstants.primaryGray50}>{[r.region, r.country_name].filter(Boolean).join(', ')}</Typography>
              </Stack>
            )} />
          </Box>
        </Stack>

        <Stack spacing={1} sx={{ mt: 1.5 }}>
          {t.locations.map((l) => (
            <Stack key={l.key} direction="row" flexWrap="wrap" alignItems="center" gap={1.5}
              sx={{ border: `1px solid ${ColorsConstants.primaryGray20}`, borderRadius: 2, px: 1.5, py: 1 }}>
              <Chip size="small" label={TYPE_LABEL[l.type]} sx={{ height: 20, fontSize: 10, fontWeight: 700 }} />
              <Typography variant="body2" fontWeight={600} sx={{ flex: '1 1 160px' }}>{l.name}</Typography>
              {l.type === 'city' && (
                <TextField select size="small" label="Radius" value={l.radiusKm || 0} sx={{ width: 130 }}
                  onChange={(e) => setLocation(l.key, { radiusKm: Number(e.target.value) })}>
                  {CITY_RADIUS_OPTIONS.filter((r) => r >= minRadius).map((r) => <MenuItem key={r} value={r}>{r ? `+${r} km` : 'City only'}</MenuItem>)}
                </TextField>
              )}
              {l.type === 'custom' && <Typography variant="caption">{l.radiusKm} km around the pin</Typography>}
              <IconButton size="small" aria-label={`Remove ${l.name}`} onClick={() => onChange({ locations: t.locations.filter((x) => x.key !== l.key) })}>
                <Close fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          {showPin
            ? <PinForm minRadius={minRadius} onAdd={(pin) => { addLocation(pin); setShowPin(false); }} onCancel={() => setShowPin(false)} />
            : (
              <Button size="small" startIcon={<Place />} onClick={() => setShowPin(true)}
                sx={{ alignSelf: 'flex-start', textTransform: 'none', border: `1px dashed ${ColorsConstants.primaryBlue100}` }}>
                Drop a pin and set radius
              </Button>
            )}
        </Stack>
      </Box>

      <Divider />

      <Box>
        <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>Age and gender</Typography>
        <Stack direction="row" flexWrap="wrap" alignItems="center" gap={3}>
          <RadioGroup row value={locked ? 'all' : t.gender} onChange={(e) => onChange({ gender: e.target.value })}>
            {[['all', 'All'], ['male', 'Male'], ['female', 'Female']].map(([v, l]) => (
              <FormControlLabel key={v} value={v} disabled={locked} control={<Radio size="small" sx={radioSx} />} label={<Typography variant="body2">{l}</Typography>} />
            ))}
          </RadioGroup>
          <Box sx={{ width: 260 }}>
            <Typography variant="caption" fontWeight={700}>
              Age {locked ? LOCKED_AGE[0] : t.ageMin} – {(locked ? LOCKED_AGE[1] : t.ageMax) >= 65 ? '65+' : t.ageMax}
            </Typography>
            <Slider
              value={locked ? LOCKED_AGE : [t.ageMin, t.ageMax]} min={18} max={65} disableSwap disabled={locked}
              onChange={(_, [min, max]) => onChange({ ageMin: min, ageMax: max })}
              sx={{ color: ColorsConstants.primaryBlue100 }}
              getAriaLabel={(i) => (i === 0 ? 'Minimum age' : 'Maximum age')}
            />
          </Box>
        </Stack>
        {locked && (
          <Stack direction="row" alignItems="center" gap={1} sx={{ p: 1.25, borderRadius: 1, backgroundColor: '#F9FAFB' }}>
            <LockOutlined sx={{ fontSize: 16 }} />
            <Note>For loans, housing or jobs ads, age is fixed at 18–65+, gender is All, and these controls are locked.</Note>
          </Stack>
        )}
      </Box>

      <Divider />

      <Box>
        <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
          Interests and behaviours <Box component="span" sx={{ fontWeight: 400, color: ColorsConstants.primaryGray50 }}>(optional)</Box>
        </Typography>
        <TextField fullWidth size="small" type="search" placeholder="e.g. Shopping, Jewellery, Recently moved" value={detQuery}
          onChange={(e) => setDetQuery(e.target.value)}
          InputProps={{ endAdornment: detLoading ? <CircularProgress size={16} /> : null }}
          inputProps={{ 'aria-label': 'Search interests, behaviours, life events' }} />
        <ResultList results={detResults} onPick={addDetailed} render={(r) => (
          <Box>
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography variant="body2">{r.name}</Typography>
              <Chip size="small" label={r.type} sx={{ height: 18, fontSize: 10 }} />
            </Stack>
            {r.path && <Typography variant="caption" color={ColorsConstants.primaryGray50}>{[].concat(r.path).join(' › ')}</Typography>}
          </Box>
        )} />
        <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1 }}>
          {t.detailed.map((d) => {
            const isRetired = retiredIds.includes(d.id);
            return (
              <Chip
                key={d.id} size="small"
                label={<span style={{ textDecoration: isRetired ? 'line-through' : 'none' }}>{d.name} <small>· {d.type}</small></span>}
                onDelete={() => onChange({ detailed: t.detailed.filter((x) => x.id !== d.id) })}
                sx={{ backgroundColor: isRetired ? '#FEE2E2' : ColorsConstants.primaryBlue60, color: isRetired ? '#991B1B' : undefined }}
              />
            );
          })}
        </Stack>
        {retired.length > 0 && (
          <Typography variant="caption" color="#991B1B" sx={{ display: 'block', mt: 1 }}>
            {retired.length} {retired.length === 1 ? 'interest is' : 'interests are'} no longer offered by Meta and will be removed before publishing.
          </Typography>
        )}
        <Note sx={{ mt: 1 }}>Only options available in the selected country and allowed for this ad category are shown.</Note>
      </Box>
    </Stack>
  );
};

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('en-IN'));

// Live audience size from the estimate endpoint.
export const AudiencePanel = ({ estimate, loading, error, targeting, specialAdCategory }) => {
  const a = estimate?.audience;
  const locked = isLockedCategory(specialAdCategory);
  return (
    <Paper elevation={0} sx={{ p: 2, border: `1px solid ${ColorsConstants.primaryGray10}`, borderRadius: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="body2" fontWeight={700}>Audience size</Typography>
        {loading && <CircularProgress size={14} />}
      </Stack>
      <Typography variant="h6" fontWeight={700} sx={{ my: 0.5 }}>
        {a ? `${fmt(a.lower)} – ${fmt(a.upper)} people` : '—'}
      </Typography>
      {estimate?.audience_label && <Note>{estimate.audience_label}</Note>}
      {error && <Note sx={{ color: '#991B1B' }}>Couldn't get an estimate: {error}</Note>}
      {!a && !error && <Note>Add a location to see the estimate.</Note>}
      <Divider sx={{ my: 1.5 }} />
      <Typography variant="caption" fontWeight={700} sx={{ display: 'block' }}>Your audience</Typography>
      <Note>{targeting.locations.map((l) => l.name + (l.radiusKm ? ` +${l.radiusKm} km` : '')).join(', ') || 'No location yet'}</Note>
      <Note>Age {locked ? '18–65+' : `${targeting.ageMin}–${targeting.ageMax >= 65 ? '65+' : targeting.ageMax}`} · {locked || targeting.gender === 'all' ? 'All genders' : targeting.gender === 'male' ? 'Men' : 'Women'}</Note>
      <Note>{targeting.detailed.length} interests · Advantage+ {targeting.advantageAudience ? 'on' : 'off'}</Note>
      <Note sx={{ mt: 1 }}>Estimate from Meta; updates as you change targeting.</Note>
    </Paper>
  );
};

export default TargetingSection;
